import { formatInTimezone } from '@kioskgaming/ui';
import type { ShopLedgerPresentation, ShopWalletLedgerRow } from '../types';

export function toNum(v: unknown): number {
  if (v === undefined || v === null) return NaN;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : NaN;
}

export function txCreatedAt(
  row: ShopWalletLedgerRow,
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  const v = row.created_at ?? row.createdAt;
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : formatInTimezone(d, timezone);
}

/** Align with backend normalizeLedgerMetadata (JSON string, snake_case). */
export function normalizeMeta(raw: unknown): Record<string, unknown> {
  if (raw == null || raw === '') return {};
  let m: unknown = raw;
  if (typeof m === 'string') {
    try {
      m = JSON.parse(m);
    } catch {
      return {};
    }
  }
  if (typeof m !== 'object' || m === null || Array.isArray(m)) return {};
  const o = { ...(m as Record<string, unknown>) };
  if (o.player_phone != null && o.playerPhone == null) o.playerPhone = o.player_phone;
  if (o.player_id != null && o.playerId == null) o.playerId = o.player_id;
  if (o.agent_id != null && o.agentId == null) o.agentId = o.agent_id;
  return o;
}

function counterpartyFromMeta(meta: Record<string, unknown>): string | null {
  if (typeof meta.playerPhone === 'string' && meta.playerPhone.trim()) return meta.playerPhone.trim();
  if (meta.playerId != null && String(meta.playerId).trim() !== '') {
    const id = String(meta.playerId).trim();
    return id.length > 10 ? `${id.slice(0, 8)}…` : id;
  }
  if (
    (meta.entryKind === 'admin_shop_adjustment' || meta.entryKind === 'credit_allocation') &&
    meta.agentId != null &&
    String(meta.agentId).trim() !== ''
  ) {
    const id = String(meta.agentId).trim();
    return id.length > 10 ? `Agent ${id.slice(0, 8)}…` : `Agent ${id}`;
  }
  return null;
}

/** Merges API `presentation` with counterparty parsed from `metadata` when server left it null. */
export function getPresentation(row: ShopWalletLedgerRow): ShopLedgerPresentation {
  const meta = normalizeMeta(row.metadata);
  const cpFromMeta = counterpartyFromMeta(meta);

  if (row.presentation?.title) {
    return {
      ...row.presentation,
      counterparty: row.presentation.counterparty ?? cpFromMeta ?? null,
    };
  }

  const before = toNum(row.balanceBefore ?? row.balance_before);
  const after = toNum(row.balanceAfter ?? row.balance_after);
  const signed =
    Number.isFinite(before) && Number.isFinite(after) ? Number((after - before).toFixed(8)) : 0;

  let direction: ShopLedgerPresentation['direction'] = 'neutral';
  if (signed > 1e-8) direction = 'in';
  else if (signed < -1e-8) direction = 'out';

  const wt = String(row.type || '');
  const desc = String(row.description || '');
  const ek = meta.entryKind;

  let title: string;
  if (ek === 'transfer_to_player') title = 'Transfer to player';
  else if (ek === 'transfer_from_player') title = 'Credit returned from player';
  else if (ek === 'admin_shop_adjustment') {
    if (signed > 1e-8) title = 'Credit from agent';
    else if (signed < -1e-8) title = 'Debit by agent';
    else title = 'Agent adjustment';
  } else if (wt === 'deposit') title = desc.includes('Shop→player') ? 'Transfer to player' : 'Deposit to wallet';
  else if (wt === 'withdrawal') title = 'Withdrawal';
  else if (wt === 'transfer') title = desc.includes('Shop→player') ? 'Transfer to player' : 'Transfer';
  else if (wt === 'bonus') title = 'Bonus';
  else if (wt === 'credit') title = 'Credit to shop';
  else if (wt === 'debit') title = 'Debit';
  else if (wt === 'adjustment') {
    if (signed > 1e-8) title = 'Balance increase';
    else if (signed < -1e-8) title = 'Balance decrease';
    else title = 'Ledger adjustment';
  } else if (wt === 'reservation') title = 'Reservation';
  else if (wt === 'reservation_release') title = 'Reservation release';
  else title = wt.replace(/_/g, ' ') || 'Ledger entry';

  return {
    title,
    direction,
    signedAmount: signed,
    counterparty: cpFromMeta,
    shortRef: row.id ? `${row.id.slice(0, 8)}…` : null,
    fullId: row.id,
  };
}

export function formatSigned(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const s = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
  return n < 0 ? `−${s}` : `+${s}`;
}

